import axios from "axios";
const API_URL = process.env.REACT_APP_API_URL || "https://api-agamhive.agamvanigam.com";
const getAuthToken = () => localStorage.getItem("authToken") || "";

export const UNAUTHORIZED_EVENT = "unauthorized_access";

interface FetchParams {
  page?: number;
  limit?: number;
  search?: string;
  filter?: Record<string, string | number | boolean>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startdate?: string;
  enddate?: string;
}

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.error("Unauthorized access. Dispatching event for navigation...");
      localStorage.removeItem("authToken");
      
      const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
      window.dispatchEvent(unauthorizedEvent);
    }
    return Promise.reject(error);
  }
);

export const getData = async <T>(endpoint: string, params: FetchParams = {}): Promise<T | null> => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.error("Unauthorized. Please log in.");
      const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
      window.dispatchEvent(unauthorizedEvent);
      return null;
    }
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      limit: (params.limit || 10).toString(),
      ...(params.search ? { search: params.search } : {}),
      ...(params.filter
        ? Object.fromEntries(Object.entries(params.filter).map(([key, value]) => [key, String(value)]))
        : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
      ...(params.startdate ? {startdate: params.startdate}:{}),
      ...(params.enddate ? {enddate: params.enddate}:{})
    });
    
    const response = await apiClient.get(`${endpoint}?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error("GET Error:", error);
    return null;
  }
};

export const getDataById = async <T>(endpoint: string, id: string | number): Promise<T | null> => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.error("Unauthorized. Please log in.");
      const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
      window.dispatchEvent(unauthorizedEvent);
      return null;
    }
    const url = `${endpoint}/${id}`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error("GET by ID Error:", error);
    return null;
  }
};

export const postData = async <T>(endpoint: string, data: object): Promise<T> => {
  try {
    const response = await apiClient.post(endpoint, data);
    return response.data;
  } catch (error: any) {
    console.error("POST Error:", error);
    if (error.response?.data) {
      throw new Error(error.response.data.msg || "Request failed");
    }
    throw error; 
  }
};

export const putData = async <T>(endpoint: string, id: string, data: object): Promise<T> => {
  try {
    const response = await apiClient.put(`${endpoint}/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error("PUT Error:", error);
    if (error.response?.data) {
      throw new Error(error.response.data.msg || "Request failed");
    }
    throw error; 
  }
};

export const patchData = async <T>(endpoint: string, id: string, data: object): Promise<T | null> => {
  try {
    const response = await apiClient.patch(`${endpoint}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("PATCH Error:", error);
    return null;
  }
};

export const deleteData = async (endpoint: string, id: string): Promise<any> => {
  try {
    const response = await apiClient.delete(`${endpoint}/${id}`);
    return response.data;
  } catch (error) {
    console.error("DELETE Error:", error);
    return false;
  }
};